import React, { useState, useEffect } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import Navbar from "../components/helpers/Navbar";
import {
  Card,
  Row,
  Col,
  Container,
  ListGroup,
  Badge,
  Spinner,
} from "react-bootstrap";
import BrevoStats from "../components/dashboard/BrevoStats";
import { supabase } from "../supabaseClient";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const TOKEN = "unwantedtok";
const AUTH_HEADER = {
  Authorization: `Bearer ${TOKEN}`,
};

const Dashboard = () => {
  const [userEmail, setUserEmail] = useState(null);
  const [nickname, setNickname] = useState(null);

  const [visitCounts, setVisitCounts] = useState({
    days7: 0,
    days30: 0,
  });
  const [visitStats7, setVisitStats7] = useState(null);
  const [visitStats30, setVisitStats30] = useState(null);
  const [trafficChart, setTrafficChart] = useState(null);

  const [stats, setStats] = useState([
    { title: "Cotizații", value: "0 RON", percent: "", color: "info" },
    { title: "Membri", value: "0", percent: "", color: "primary" },
    { title: "Voluntari", value: "0", percent: "", color: "success" },
  ]);
  const [activitatiRecente, setActivitatiRecente] = useState([]);
  const [versionInfo, setVersionInfo] = useState(null);
  const [loadingVersion, setLoadingVersion] = useState(true);

  useEffect(() => {
    const fetchVolunteerCount = async () => {
      try {
        const res = await fetch(
          "https://prdp-api.suport-949.workers.dev/api/voluntari",
          { headers: AUTH_HEADER }
        );
        if (!res.ok) throw new Error("Eroare la preluare voluntari");
        const data = await res.json();
        setStats((prevStats) =>
          prevStats.map((item) =>
            item.title === "Voluntari"
              ? { ...item, value: data.length.toString() }
              : item
          )
        );
      } catch (err) {
        console.error("Eroare la preluarea numărului de voluntari:", err);
      }
    };

    const fetchMemberCount = async () => {
      try {
        const res = await fetch(
          "https://prdp-api.suport-949.workers.dev/api/membri",
          { headers: AUTH_HEADER }
        );
        if (!res.ok) throw new Error("Eroare la preluare membri");
        const data = await res.json();
        setStats((prevStats) =>
          prevStats.map((item) =>
            item.title === "Membri"
              ? { ...item, value: data.length.toString() }
              : item
          )
        );
      } catch (err) {
        console.error("Eroare la preluarea numărului de membri:", err);
      }
    };

    const fetchLogs = async () => {
      try {
        const response = await fetch(
          "https://prdp-api.suport-949.workers.dev/api/logs",
          { headers: AUTH_HEADER }
        );
        if (response.ok) {
          const data = await response.json();
          setActivitatiRecente(data);
        } else {
          console.error("Eroare la preluarea activităților recente");
        }
      } catch (err) {
        console.error("Eroare la fetch logs:", err);
      }
    };

    const fetchVersionInfo = async () => {
      try {
        const res = await fetch("https://admin.prdp.ro/version.json");
        if (res.ok) {
          const data = await res.json();
          setVersionInfo(data);
        } else {
          console.error("Eroare la preluarea informațiilor despre versiune");
        }
      } catch (err) {
        console.error("Eroare la fetch version info:", err);
      } finally {
        setLoadingVersion(false);
      }
    };

    // Fetch vizite stats & grafice
    const fetchVisits = async () => {
      try {
        // Stats 7 zile
        const res7 = await fetch(
          "https://prdp-api.suport-949.workers.dev/api/vizite?days=7",
          { headers: AUTH_HEADER }
        );
        const data7 = res7.ok ? await res7.json() : { total: 0, statsPeOras: [] };

        // Stats 30 zile
        const res30 = await fetch(
          "https://prdp-api.suport-949.workers.dev/api/vizite?days=30",
          { headers: AUTH_HEADER }
        );
        const data30 = res30.ok ? await res30.json() : { total: 0, statsPeOras: [] };

        setVisitCounts({ days7: data7.total, days30: data30.total });
        setVisitStats7(data7);
        setVisitStats30(data30);

        // Pregătim și datele pentru grafic
        if (data30.traficZilnic && Array.isArray(data30.traficZilnic)) {
          setTrafficChart({
            labels: data30.traficZilnic.map(d =>
              d.date.slice(8, 10) + "." + d.date.slice(5, 7)
            ),
            datasets: [
              {
                label: "Vizite zilnic",
                data: data30.traficZilnic.map(d => d.visits),
                fill: false,
                borderColor: "#01358d",
                tension: 0.1,
              },
            ],
          });
        }
      } catch (err) {
        console.error("Eroare la preluarea datelor vizite:", err);
        setVisitCounts({ days7: 0, days30: 0 });
        setVisitStats7(null);
        setVisitStats30(null);
        setTrafficChart(null);
      }
    };

    fetchVolunteerCount();
    fetchMemberCount();
    fetchVisits();
    fetchLogs();
    fetchVersionInfo();

    const { data: authListener } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (session?.user?.email) {
          const user = session.user;
          setUserEmail(user.email);
          const fetchNickname = async () => {
            const { data, error } = await supabase
              .from("profiles")
              .select("nickname")
              .eq("id", user.id)
              .single();
            if (!error && data?.nickname) {
              setNickname(data.nickname);
            }
          };
          fetchNickname();
        } else {
          setUserEmail(null);
          setNickname(null);
        }
      }
    );

    return () => {
      if (authListener?.subscription) {
        authListener.subscription.unsubscribe();
      }
    };
  }, []);

  const trafficOptions = {
    responsive: true,
    plugins: {
      legend: { display: true, position: "top" },
      title: {
        display: false,
        text: "Vizite zilnic (30 zile)",
      },
    },
    scales: {
      x: {
        ticks: { color: "#666", maxRotation: 45, minRotation: 45 },
      },
      y: {
        ticks: { color: "#666" },
        beginAtZero: true,
      },
    },
  };

  const VisitStatsCard = ({ title, data }) => {
    if (!data)
      return (
        <Card className="shadow-sm h-100">
          <Card.Body className="text-center">
            <Spinner animation="border" />
            <div>Se încarcă...</div>
          </Card.Body>
        </Card>
      );

    return (
      <Card className="shadow-sm h-100">
        <Card.Body>
          <Card.Title className="mb-3">{title}</Card.Title>
          <div className="total-visits-stats h5">Total vizite: {data.total}</div>
          <ListGroup variant="flush" style={{ maxHeight: "200px", overflowY: "auto" }}>
            {data.statsPeOras && data.statsPeOras.length > 0 ? (
              data.statsPeOras.map(({ oras, count }) => (
                <ListGroup.Item
                  key={oras}
                  className="d-flex justify-content-between align-items-center"
                >
                 <p className="oras-field"> {oras} </p>
                  <Badge bg="primary" pill>
                    {count}
                  </Badge>
                </ListGroup.Item>
              ))
            ) : (
              <ListGroup.Item>Nu există date despre vizite</ListGroup.Item>
            )}
          </ListGroup>
        </Card.Body>
      </Card>
    );
  };

  return (
    <div className="flex-wrapper">
      <Navbar />
      <Container fluid className="py-4">
        <h3 className="mb-4 fw-bold ml-5 text-primary-dark">
          Bine ai venit{nickname ? `, ${nickname}` : userEmail ? `, ${userEmail}` : ""}!
        </h3>

        <Row className="g-4 mb-4">
          <Col xs={12} sm={6} lg={3}>
            <Card className="shadow-sm h-100">
              <Card.Body>
                <Card.Title className="mb-1 fs-6 text-muted">Vizite (ultimele 7 zile)</Card.Title>
                <h4 className="fw-bold">{visitCounts.days7}</h4>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} sm={6} lg={3}>
            <Card className="shadow-sm h-100">
              <Card.Body>
                <Card.Title className="mb-1 fs-6 text-muted">Vizite (ultimele 30 zile)</Card.Title>
                <h4 className="fw-bold">{visitCounts.days30}</h4>
              </Card.Body>
            </Card>
          </Col>
          {stats.map((item, idx) => (
            <Col key={idx} xs={12} sm={6} lg={3}>
              <Card className="shadow-sm h-100">
                <Card.Body>
                  <Card.Title className="mb-1 fs-6 text-muted">{item.title}</Card.Title>
                  <h4 className="fw-bold">{item.value}</h4>
                  {item.percent && (
                    <small className={`text-${item.color}`}>{item.percent}</small>
                  )}
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        <Row className="g-4">
          <Col xs={12} md={8}>
            <Card className="shadow-sm h-100">
              <Card.Body>
                <Card.Title className="mb-3">Trafic Site (vizite zilnic, 30 zile)</Card.Title>
                {trafficChart ? (
                  trafficChart.labels && trafficChart.labels.length > 0 ? (
                    <Line data={trafficChart} options={trafficOptions} height={100} />
                  ) : (
                    <div>Nu s-au putut încărca datele pentru grafic.</div>
                  )
                ) : (
                  <div>Se încarcă datele pentru grafic...</div>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col xs={12} md={4}>
            <Card className="shadow-sm h-100">
              <Card.Body>
                <Card.Title className="mb-3">Activitate Recentă</Card.Title>
                <ListGroup variant="flush">
                  {activitatiRecente.length > 0 ? (
                    activitatiRecente.map((item, idx) => (
                      <ListGroup.Item
                        key={idx}
                        className="d-flex justify-content-between align-items-start"
                      >
                        <div>
                          <div className="fw-semibold">
                            {item.prenume} {item.nume}
                          </div>
                          <small className="text-muted">{item.actiune}</small>
                        </div>
                        <Badge bg="light" text="dark" pill>
                          {item.data}
                        </Badge>
                      </ListGroup.Item>
                    ))
                  ) : (
                    <ListGroup.Item className="text-muted text-center">
                      Nicio activitate recentă
                    </ListGroup.Item>
                  )}
                </ListGroup>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="g-4 mt-5">
          <Col xs={12} md={4}>
            <BrevoStats />
          </Col>
          <Col xs={12} md={4}>
            <VisitStatsCard title="Statistici Vizite (ultimele 7 zile)" data={visitStats7} />
          </Col>
          <Col xs={12} md={4}>
            <VisitStatsCard title="Statistici Vizite (ultimele 30 zile)" data={visitStats30} />
          </Col>
        </Row>

        <Row className="g-4 mt-5">
          <Col xs={12} md={12}>
            <Card className="shadow-sm h-100">
              <Card.Body>
                <Card.Title className="mb-3">Admin Website</Card.Title>
                <ListGroup variant="flush">
                  {loadingVersion ? (
                    <ListGroup.Item className="text-muted text-center">
                      <Spinner animation="border" size="sm" /> Se încarcă versiunea...
                    </ListGroup.Item>
                  ) : versionInfo ? (
                    <>
                      <ListGroup.Item className="d-flex justify-content-between align-items-center">
                        <span className="fw-semibold">Versiune</span>
                        <Badge bg="light" text="dark" pill>
                          {versionInfo.version}
                        </Badge>
                      </ListGroup.Item>
                      <ListGroup.Item className="d-flex justify-content-between align-items-center">
                        <span className="fw-semibold">Actualizare:</span>
                        <Badge bg="light" text="dark" pill>
                          {versionInfo.commit.message}
                        </Badge>
                      </ListGroup.Item>
                      <ListGroup.Item className="d-flex justify-content-between align-items-center">
                        <span className="fw-semibold">Data/ora actualizare</span>
                        <Badge bg="light" text="dark" pill>
                          {versionInfo.commit.datetime}
                        </Badge>
                      </ListGroup.Item>
                    </>
                  ) : (
                    <ListGroup.Item className="text-danger text-center">
                      Nu s-au putut încărca informațiile despre versiune.
                    </ListGroup.Item>
                  )}
                </ListGroup>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Dashboard;
